import { Injectable } from '@angular/core';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Document } from '../../../models/document';
import { MergeTypeEnum } from '../../../enum/merge-type.enum';
import { DocumentTypeEnum } from '../../../enum/document-type.enum';
import { environment } from '../../../../environments/environment';
import { catchError, filter, map } from 'rxjs/operators';
import { interfaceDocumentClear, interfaceDocumentProcessing, interfaceDocumentReady } from '../../../store/interface/interface.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class DocumentService {

    constructor(private store: Store<AppState>, private http: HttpClient, private snackbar: MatSnackBar) {
    }

    openShipmentWaybills(ids: string[]): void {
        this.store.dispatch(interfaceDocumentProcessing());
        const doc: Document = {
            filename: null,
            mimeType: 'application/pdf',
            type: DocumentTypeEnum.WAYBILL,
            shipmentId: null,
            mergeType: MergeTypeEnum.SHIPMENTS,
            mergeIds: ids
        };
        this.http.post<Document>(`${environment.apiUrl}/documents`, doc).pipe(
            map((document) => {
                this.store.dispatch(interfaceDocumentReady({ document }));
            }),
            catchError((error) => {
                this.snackbar.open($localize`Unable to compile the requested documents.  Note that you cannot combine different document types.`);
                this.store.dispatch(interfaceDocumentClear());
                throw error;
            })
        ).subscribe();
    }

    openInvoices(ids: string[]): void {
        this.store.dispatch(interfaceDocumentProcessing());
        const doc: Document = {
            filename: null,
            mimeType: 'application/pdf',
            type: DocumentTypeEnum.INVOICE,
            shipmentId: null,
            mergeType: MergeTypeEnum.INVOICES,
            mergeIds: ids
        };
        this.http.post<Document>(`${environment.apiUrl}/documents`, doc).pipe(
            map((document) => {
                this.store.dispatch(interfaceDocumentReady({ document }));
            }),
            catchError((error) => {
                this.snackbar.open($localize`Unable to compile the requested documents.  Note that you cannot combine different document types.`);
                this.store.dispatch(interfaceDocumentClear());
                throw error;
            })
        ).subscribe();
    }

    createShipmentUpload(shipmentId: string, file: File): Observable<Document> {
        const mimeType = this.resolveMimeType(file);
        if (!mimeType) {
            return throwError(() => new Error('Unsupported document type.'));
        }
        const doc: Document = {
            type: DocumentTypeEnum.UPLOAD,
            shipmentId,
            filename: file.name,
            mimeType
        };
        return this.http.post<Document>(`${environment.apiUrl}/documents`, doc);
    }

    uploadFileToUrl(url: string, file: File): Observable<number> {
        const mimeType = this.resolveMimeType(file);
        if (!mimeType) {
            return throwError(() => new Error('Unsupported document type.'));
        }
        const headers = new HttpHeaders({ 'Content-Type': mimeType });
        return this.http.put(url, file, {
            observe: 'events',
            reportProgress: true,
            headers
        }).pipe(
            map((event: HttpEvent<any>) => {
                if (event.type === HttpEventType.UploadProgress) {
                    if (!event.total) {
                        return 0;
                    }
                    return Math.round((100 * event.loaded) / event.total);
                }
                if (event.type === HttpEventType.Response) {
                    return 100;
                }
                return null;
            }),
            filter(progress => progress !== null)
        );
    }

    setDocumentType(id: string, type: DocumentTypeEnum): Observable<Document> {
        return this.http.put<Document>(`${environment.apiUrl}/documents/${id}`, { type });
    }

    deleteDocument(id: string): Observable<void> {
        return this.http.delete<void>(`${environment.apiUrl}/documents/${id}`);
    }

    private resolveMimeType(file: File): string | null {
        if (file.type === 'application/pdf' || file.type === 'image/png' || file.type === 'image/jpeg') {
            return file.type;
        }
        const name = file.name.toLowerCase();
        if (name.endsWith('.pdf')) {
            return 'application/pdf';
        }
        if (name.endsWith('.png')) {
            return 'image/png';
        }
        if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
            return 'image/jpeg';
        }
        return null;
    }

}
